import { addDays, addMonths, diffDays, monthKey, startOfMonth } from "./dates";
import { topicKey } from "./sessions";
import type { Category, ISODate, PresenterRef, Session } from "./types";

export type InsightPeriod = "3m" | "6m" | "year" | "all";

export const PERIOD_LABEL: Record<InsightPeriod, string> = {
  "3m": "Last 3 months",
  "6m": "Last 6 months",
  year: "This year",
  all: "All time",
};

export function periodStart(period: InsightPeriod, today: ISODate): ISODate | null {
  switch (period) {
    case "3m":
      return addDays(today, -91);
    case "6m":
      return addDays(today, -182);
    case "year":
      return `${today.slice(0, 4)}-01-01`;
    case "all":
      return null;
  }
}

export interface CategoryCount {
  category: Category;
  count: number;
  pct: number;
}

export interface TrendMonth {
  month: string;
  total: number;
  byCategory: Record<string, number>;
}

export interface TopTopic {
  topic: string;
  count: number;
  categoryId: string | null;
  presenters: PresenterRef[];
  sessionIds: string[];
}

export interface InsightsResult {
  sessions: Session[];
  total: number;
  uniqueTopics: number;
  categoriesUsed: number;
  activeCategoryCount: number;
  top?: CategoryCount;
  distribution: CategoryCount[];
  trend: TrendMonth[];
  topTopics: TopTopic[];
  /** Active categories with no sharing in the last 4 weeks. */
  gaps: Category[];
}

/** Minimum completed sessions before insights are meaningful. */
export const INSIGHTS_MIN_SESSIONS = 3;

export function completedSessions(sessions: Session[]): Session[] {
  return sessions.filter((s) => s.status === "completed");
}

export function computeInsights(
  allSessions: Session[],
  categories: Category[],
  today: ISODate,
  period: InsightPeriod,
): InsightsResult {
  const start = periodStart(period, today);
  const completed = completedSessions(allSessions).filter(
    (s) => diffDays(s.date, today) <= 0 && (!start || diffDays(s.date, start) >= 0),
  );
  const total = completed.length;

  const counts = new Map<string, number>();
  for (const s of completed) {
    const key = s.categoryId ?? "uncategorised";
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  const distribution: CategoryCount[] = [...categories]
    .filter((c) => c.active || counts.has(c.id))
    .map((category) => {
      const count = counts.get(category.id) ?? 0;
      return { category, count, pct: total ? (count / total) * 100 : 0 };
    })
    .sort((a, b) => b.count - a.count || a.category.order - b.category.order);

  const used = distribution.filter((d) => d.count > 0);

  // Trend months: from period start (or first session) to current month.
  const firstMonth = start ? startOfMonth(start) : completed.length ? startOfMonth(completed.reduce((min, s) => (s.date < min ? s.date : min), completed[0].date)) : startOfMonth(today);
  const months: string[] = [];
  let cursor = firstMonth;
  let guard = 0;
  while (monthKey(cursor) <= monthKey(today) && guard < 36) {
    months.push(monthKey(cursor));
    cursor = addMonths(cursor, 1);
    guard += 1;
  }
  const trend: TrendMonth[] = months.map((month) => {
    const byCategory: Record<string, number> = {};
    let monthTotal = 0;
    for (const s of completed) {
      if (monthKey(s.date) !== month) continue;
      const key = s.categoryId ?? "uncategorised";
      byCategory[key] = (byCategory[key] ?? 0) + 1;
      monthTotal += 1;
    }
    return { month, total: monthTotal, byCategory };
  });

  const topicMap = new Map<string, TopTopic>();
  for (const s of completed) {
    const key = topicKey(s.topic);
    if (!key) continue;
    const entry = topicMap.get(key) ?? { topic: s.topic, count: 0, categoryId: s.categoryId, presenters: [], sessionIds: [] };
    entry.count += 1;
    entry.presenters.push(s.presenter);
    entry.sessionIds.push(s.id);
    topicMap.set(key, entry);
  }
  const topTopics = [...topicMap.values()]
    .sort((a, b) => b.count - a.count || a.topic.localeCompare(b.topic))
    .slice(0, 10);

  const recentCutoff = addDays(today, -28);
  const recent = completedSessions(allSessions).filter((s) => diffDays(s.date, recentCutoff) >= 0 && diffDays(s.date, today) <= 0);
  const recentCats = new Set(recent.map((s) => s.categoryId));
  const gaps = categories.filter((c) => c.active && !recentCats.has(c.id)).sort((a, b) => a.order - b.order);

  return {
    sessions: completed,
    total,
    uniqueTopics: topicMap.size,
    categoriesUsed: used.length,
    activeCategoryCount: categories.filter((c) => c.active).length,
    top: used[0],
    distribution,
    trend,
    topTopics,
    gaps,
  };
}
